import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faXmark, faCheck, faSpinner, faSave } from "@fortawesome/free-solid-svg-icons";
import { ServiceManager } from "../../../../services/ServiceManager";
import { APIStatusCode, Permissions } from "../../../../app/enums";
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import MISButton from "../../../../components/button";
import Layout from "../../../layout";
import { normaliseCustomFieldPaths } from "./custom-field-utils";

export default function ConfigureCustomFields() {
  const { establishmentId, clientId } = useParams<{
    establishmentId: string;
    clientId: string;
  }>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = usePermissionCheck(Permissions.CanEditEstablishments);

  // ─── QUERIES ────────────────────────────────────────────────────────────────

  const { data: systemFields = [], isSuccess: systemLoaded } = useQuery<string[]>({
    queryKey: ["additional-fields", establishmentId, clientId],
    enabled: !!establishmentId && !!clientId,
    queryFn: async () => {
      const res = await ServiceManager.EstablishmentService.GetAdditionalProperties(
        establishmentId!,
        clientId!
      );
      return normaliseCustomFieldPaths(res);
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { data: savedFields = [], isSuccess: savedLoaded } = useQuery<string[]>({
    queryKey: ["saved-custom-fields", establishmentId, clientId],
    enabled: !!establishmentId,
    queryFn: async () => {
      const res = await ServiceManager.ConfigurationService.GetCustomFieldConfiguration(
        establishmentId!,
        clientId!
      );
      return normaliseCustomFieldPaths(res);
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  // ─── STATE ──────────────────────────────────────────────────────────────────

  const [initialized, setInitialized] = useState(false);

  // What's currently checked in the UI
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // The last-saved snapshot — hasChanges() diffs against this
  const [baselineFields, setBaselineFields] = useState<string[]>([]);

  // Tags typed but not yet saved
  const [pendingTags, setPendingTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Custom fields that have been saved (get "custom" badge in Available Properties)
  const [savedCustomFields, setSavedCustomFields] = useState<string[]>([]);

  const tagInputRef = useRef<HTMLInputElement>(null);

  // ─── INIT (runs once when both queries load) ─────────────────────────────────

  useEffect(() => {
    // Wait until both queries have returned data, and only init once

    if (initialized) return;
    if (!systemLoaded || !savedLoaded) return;
    const systemSet = new Set(systemFields.map((field) => field.toLowerCase()));
    const custom = savedFields.filter((field) => !systemSet.has(field.toLowerCase()));
    setSelectedFields(savedFields);
    setBaselineFields(savedFields);
    setSavedCustomFields(custom);
    setInitialized(true);
  }, [systemFields, savedFields, initialized, systemLoaded, savedLoaded]);

  // ─── AVAILABLE PROPERTIES ───────────────────────────────────────────────────

  const availableProperties = useMemo(() => {
    return Array.from(
      new Set([...systemFields, ...savedCustomFields])
    );
  }, [systemFields, savedCustomFields]);

  // ─── HANDLERS ───────────────────────────────────────────────────────────────

  const toggleField = (field: string) => {
    setSelectedFields((prev) =>
      prev.includes(field) ? prev.filter((x) => x !== field) : [...prev, field]
    );
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const value = tagInput.trim();
      if (!pendingTags.includes(value) && !availableProperties.includes(value)) {
        setPendingTags((prev) => [...prev, value]);
      }
      setTagInput("");
    } else if (e.key === "Backspace" && tagInput === "" && pendingTags.length > 0) {
      setPendingTags((prev) => prev.slice(0, -1));
    }
  };

  const removePendingTag = (tag: string) => {
    setPendingTags((prev) => prev.filter((t) => t !== tag));
  };

  // ─── CHANGE DETECTION ───────────────────────────────────────────────────────

  const hasChanges = () => {
    const current = [
      ...selectedFields,
      ...pendingTags.filter((x) => x.trim()),
    ].sort();
    const baseline = [...baselineFields].sort();
    return JSON.stringify(current) !== JSON.stringify(baseline);
  };

  // ─── MUTATION ───────────────────────────────────────────────────────────────

  const mutation = useMutation({
    mutationFn: (model: any) =>
      ServiceManager.ConfigurationService.SaveCustomFieldConfiguration(
        establishmentId!,
        clientId!,
        model
      ),

    onSuccess: (data, variables) => {
      if (data?.status === APIStatusCode.Ok) {
        ServiceManager.ToastService.showSuccess("Saved successfully. Perform a force refresh to see the updated changes.");

        // The fields we just saved successfully
        const justSaved: string[] = variables.fieldPaths;
        const system = systemFields;

        // Recalculate custom fields from scratch based on what was just saved
        // This removes any custom fields that were unchecked and saved
        const newCustom = justSaved.filter((f) => !system.includes(f));
        setSavedCustomFields(newCustom);

        // Update checked state to exactly what was saved
        setSelectedFields(justSaved);

        // Update baseline so hasChanges() resets correctly
        setBaselineFields(justSaved);

        // Clear pending tags — they're now in the list
        setPendingTags([]);
        setTagInput("");

        // Background refetch to keep React Query cache in sync
        queryClient.invalidateQueries({
          queryKey: ["saved-custom-fields", establishmentId, clientId],
        });
      } else {
        ServiceManager.ToastService.showError("Save failed");
      }
    },

    onError: () => {
      ServiceManager.ToastService.showError("Unable to save custom fields.");
    },
  });

  const handleSave = () => {
    const fieldPaths = [
      ...selectedFields,
      ...pendingTags.filter((x) => x.trim()),
    ];
    mutation.mutate({ fieldPaths });
  };

  // ─── UI ─────────────────────────────────────────────────────────────────────

  return (
    <Layout
      title="Configure Custom Fields"
      backArrow={
        <FontAwesomeIcon
          icon={faArrowLeft}
          onClick={() => navigate(-1)}
          className="me-4 fs-5 cursor-pointer"
        />
      }
      button={
        canEdit ? (
          <MISButton
            text={mutation.isPending ? "Saving..." : "Save"}
            icon={
                <FontAwesomeIcon
                icon={mutation.isPending ? faSpinner : faSave}
                spin={mutation.isPending}
                className="font-14"
                />
            }
            className="btn btn-primary crbc-bg-color me-2"
            FnOnClick={handleSave}
            disabled={mutation.isPending || !hasChanges()}
          />
        ) : undefined
      }
    >
      <div style={{ padding: "1.5rem" }}>

        {/* ── AVAILABLE PROPERTIES ── */}
        <section style={{ marginBottom: "2rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: "0.75rem",
            }}
          >
            <div>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                Available Properties
              </h5>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6c757d" }}>
                Select the fields you want to enable
              </p>
            </div>
            {availableProperties.length > 0 && (
              <span
                style={{
                  fontSize: 12,
                  color: "#6c757d",
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: 20,
                  padding: "2px 10px",
                  whiteSpace: "nowrap",
                  marginTop: 2,
                }}
              >
                {selectedFields.filter((f) => availableProperties.includes(f)).length}
                {" / "}
                {availableProperties.length} selected
              </span>
            )}
          </div>

          {availableProperties.length > 0 ? (
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 8,
                background: "#fff",
                maxHeight: 500,
                overflowY: "auto",
              }}
            >
              {availableProperties.map((field, idx) => {
                const isChecked = selectedFields.includes(field);
                const isCustom = savedCustomFields.includes(field);
                return (
                  <label
                    key={field}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 16px",
                      cursor: canEdit ? "pointer" : "default",
                      borderBottom:
                        idx < availableProperties.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      background: isChecked ? "#eff6ff" : "transparent",
                      transition: "background 0.15s",
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => canEdit && toggleField(field)}
                      style={{ display: "none" }}
                    />

                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: isChecked
                          ? "1.5px solid #3b82f6"
                          : "1.5px solid #ced4da",
                        background: isChecked ? "#dbeafe" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.15s",
                      }}
                    >
                      {isChecked && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          style={{ fontSize: 10, color: "#2563eb" }}
                        />
                      )}
                    </span>

                    <span style={{ fontSize: 13.5, flex: 1 }}>{field}</span>

                    {isCustom && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#2563eb",
                          background: "#dbeafe",
                          border: "1px solid #bfdbfe",
                          borderRadius: 12,
                          padding: "1px 8px",
                        }}
                      >
                        custom
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                padding: "1rem 1.25rem",
                borderRadius: 8,
                background: "#fff8e1",
                border: "1px solid #ffe082",
                fontSize: 13.5,
                color: "#7c6000",
              }}
            >
              No system properties found
            </div>
          )}
        </section>

        {/* ── CUSTOM FIELDS ── */}
        {canEdit && (
          <section>
            <div style={{ marginBottom: "0.75rem" }}>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                Custom Fields
              </h5>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6c757d" }}>
                Type a field name and press{" "}
                <kbd
                  style={{
                    fontSize: 11,
                    background: "#0b5ed7",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    padding: "1px 5px",
                  }}
                >
                  Enter
                </kbd>{" "}
                to add it
              </p>
            </div>

            <div
              onClick={() => tagInputRef.current?.focus()}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                padding: "10px 12px",
                border: "1px solid #ced4da",
                borderRadius: 6,
                background: "#fff",
                minHeight: 48,
                cursor: "text",
              }}
            >
              {pendingTags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 13,
                    padding: "3px 10px",
                    borderRadius: 20,
                    background: "#dbeafe",
                    border: "1px solid #bfdbfe",
                    color: "#1d4ed8",
                    fontWeight: 500,
                  }}
                >
                  {tag}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removePendingTag(tag);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      color: "#1d4ed8",
                      opacity: 0.7,
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} style={{ fontSize: 11 }} />
                  </button>
                </span>
              ))}

              <input
                ref={tagInputRef}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder={
                  pendingTags.length === 0 ? "e.g. CustomProperty.Name" : ""
                }
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13.5,
                  flex: 1,
                  minWidth: 160,
                  color: "inherit",
                }}
              />
            </div>

            {pendingTags.length > 0 && (
              <p style={{ marginTop: 6, fontSize: 12, color: "#6c757d" }}>
                {pendingTags.length} field{pendingTags.length > 1 ? "s" : ""} ready
                to save — click Save to add{" "}
                {pendingTags.length > 1 ? "them" : "it"} to Available Properties
              </p>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}